import type { CollabStatus } from '../collab/collabClient';
import type { PeerInfo } from '../../server/protocol';

export interface ShareOverlayActions {
  /** „Session starten“ bzw. „Beitreten“ — die App entscheidet anhand der URL. */
  onStart(name: string): void;
  onRename(name: string): void;
  onLeave(): void;
  /** Folgen-Modus für einen Teilnehmer an-/ausschalten. */
  onFollowToggle(clientId: string): void;
  /** Teilnehmer bitten, der eigenen Ansicht zu folgen (Popup beim Gegenüber). */
  onFollowInvite(clientId: string): void;
  /** Alle anderen Teilnehmer einladen. */
  onInviteAll(): void;
}

/**
 * Teilen-Dialog: vor dem Beitritt Name eingeben, danach Link kopieren und
 * Teilnehmerliste sehen. Nutzt das bestehende Overlay-Muster (ShortcutOverlay).
 */
export class ShareOverlay {
  el: HTMLElement;
  private visible = false;
  private setupBox: HTMLDivElement;
  private activeBox: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private renameInput: HTMLInputElement;
  private linkInput: HTMLInputElement;
  private peersBox: HTMLDivElement;
  private statusText: HTMLDivElement;
  private errorText: HTMLDivElement;
  private selfName = '';

  get isVisible(): boolean {
    return this.visible;
  }

  constructor(root: HTMLElement, private actions: ShareOverlayActions) {
    this.el = document.createElement('div');
    this.el.className = 'overlay hidden';
    const box = document.createElement('div');
    box.className = 'overlay-box share-box';

    const h2 = document.createElement('h2');
    h2.textContent = 'Zusammen zeichnen';
    box.appendChild(h2);

    // ---- Vor dem Beitritt ----
    this.setupBox = document.createElement('div');
    const nameRow = document.createElement('label');
    nameRow.className = 'share-row';
    nameRow.append('Dein Name');
    this.nameInput = document.createElement('input');
    this.nameInput.type = 'text';
    this.nameInput.maxLength = 32;
    this.nameInput.placeholder = 'z. B. Julian';
    this.nameInput.value = localStorage.getItem('campsketch.user.name') ?? '';
    nameRow.appendChild(this.nameInput);
    this.setupBox.appendChild(nameRow);

    const startBtn = document.createElement('button');
    startBtn.className = 'top-btn share-primary';
    startBtn.textContent = 'Beitreten';
    const start = (): void => {
      const name = this.nameInput.value.trim() || 'Gast';
      localStorage.setItem('campsketch.user.name', name);
      this.actions.onStart(name);
    };
    startBtn.onclick = start;
    this.nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') start();
    });
    this.setupBox.appendChild(startBtn);

    const setupNote = document.createElement('p');
    setupNote.className = 'panel-note';
    setupNote.textContent = 'Alle mit dem Link bearbeiten dasselbe Modell gleichzeitig. Ohne Session bleibst du lokal.';
    this.setupBox.appendChild(setupNote);

    this.errorText = document.createElement('p');
    this.errorText.className = 'share-error';
    this.setupBox.appendChild(this.errorText);
    box.appendChild(this.setupBox);

    // ---- Verbunden ----
    this.activeBox = document.createElement('div');
    this.activeBox.hidden = true;

    const linkRow = document.createElement('label');
    linkRow.className = 'share-row';
    linkRow.append('Einladungslink');
    const linkLine = document.createElement('div');
    linkLine.className = 'share-link-line';
    this.linkInput = document.createElement('input');
    this.linkInput.type = 'text';
    this.linkInput.readOnly = true;
    const copyBtn = document.createElement('button');
    copyBtn.className = 'top-btn';
    copyBtn.textContent = 'Kopieren';
    copyBtn.onclick = () => {
      this.linkInput.select();
      void navigator.clipboard?.writeText(this.linkInput.value).then(
        () => {
          copyBtn.textContent = 'Kopiert!';
          setTimeout(() => (copyBtn.textContent = 'Kopieren'), 1500);
        },
        () => document.execCommand('copy')
      );
    };
    linkLine.append(this.linkInput, copyBtn);
    linkRow.appendChild(linkLine);
    this.activeBox.appendChild(linkRow);

    const peersTitleRow = document.createElement('div');
    peersTitleRow.className = 'share-peers-head';
    const peersTitle = document.createElement('h3');
    peersTitle.textContent = 'Teilnehmer';
    const inviteAllBtn = document.createElement('button');
    inviteAllBtn.type = 'button';
    inviteAllBtn.className = 'top-btn invite-btn';
    inviteAllBtn.textContent = 'Alle einladen';
    inviteAllBtn.title = 'Alle auffordern, deiner Ansicht zu folgen';
    inviteAllBtn.onclick = () => this.actions.onInviteAll();
    peersTitleRow.append(peersTitle, inviteAllBtn);
    this.activeBox.appendChild(peersTitleRow);
    this.peersBox = document.createElement('div');
    this.peersBox.className = 'share-peers';
    this.activeBox.appendChild(this.peersBox);

    const followNote = document.createElement('p');
    followNote.className = 'panel-note';
    followNote.textContent = '„Folgen“ spiegelt die Ansicht eines Teilnehmers — du siehst live, was er sieht. Eigene Kamerabewegung beendet das Folgen.';
    this.activeBox.appendChild(followNote);

    const renameRow = document.createElement('label');
    renameRow.className = 'share-row';
    renameRow.append('Name ändern');
    const renameLine = document.createElement('div');
    renameLine.className = 'share-link-line';
    this.renameInput = document.createElement('input');
    this.renameInput.type = 'text';
    this.renameInput.maxLength = 32;
    const renameBtn = document.createElement('button');
    renameBtn.className = 'top-btn';
    renameBtn.textContent = 'Übernehmen';
    const doRename = (): void => {
      const name = this.renameInput.value.trim();
      if (name && name !== this.selfName) this.actions.onRename(name);
    };
    renameBtn.onclick = doRename;
    this.renameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doRename();
    });
    renameLine.append(this.renameInput, renameBtn);
    renameRow.appendChild(renameLine);
    this.activeBox.appendChild(renameRow);

    const leaveBtn = document.createElement('button');
    leaveBtn.className = 'top-btn share-leave';
    leaveBtn.textContent = 'Session verlassen';
    leaveBtn.onclick = () => this.actions.onLeave();
    this.activeBox.appendChild(leaveBtn);
    box.appendChild(this.activeBox);

    this.statusText = document.createElement('div');
    this.statusText.className = 'share-status';
    box.appendChild(this.statusText);

    const close = document.createElement('button');
    close.className = 'top-btn overlay-close';
    close.textContent = 'Schließen';
    close.onclick = () => this.hide();
    box.appendChild(close);

    this.el.appendChild(box);
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.hide();
    });
    root.appendChild(this.el);
  }

  toggle(): void {
    this.visible ? this.hide() : this.show();
  }

  show(): void {
    this.visible = true;
    this.el.classList.remove('hidden');
  }

  hide(): void {
    this.visible = false;
    this.el.classList.add('hidden');
  }

  /** Beitreten-Modus anzeigen (Link mit ?room= geöffnet, noch kein Name gespeichert). */
  showJoin(): void {
    this.setupBox.hidden = false;
    this.activeBox.hidden = true;
    this.setError(null);
    this.setStatus('offline');
    this.nameInput.focus();
    this.show();
  }

  /** Nach dem Verbinden: aktive Session anzeigen. */
  setSession(url: string | null): void {
    if (url) {
      this.linkInput.value = url;
      this.setupBox.hidden = true;
      this.activeBox.hidden = false;
      this.setError(null);
      this.renameInput.value = this.selfName;
    } else {
      this.setupBox.hidden = false;
      this.activeBox.hidden = true;
      this.setPeers([], null);
    }
  }

  setPeers(peers: PeerInfo[], you: PeerInfo | null, followingId: string | null = null): void {
    this.selfName = you?.name ?? this.selfName;
    this.peersBox.replaceChildren(
      ...peers.map((p) => {
        const chip = document.createElement('span');
        chip.className = 'share-peer-chip';
        if (p.clientId === you?.clientId) chip.classList.add('is-you');
        chip.style.setProperty('--peer-color', p.color);
        chip.textContent = p.clientId === you?.clientId ? `${p.name} (du)` : p.name;
        if (p.clientId !== you?.clientId) {
          const btn = document.createElement('button');
          btn.type = 'button';
          const isFollowing = followingId === p.clientId;
          btn.className = isFollowing ? 'follow-btn is-following' : 'follow-btn';
          btn.textContent = isFollowing ? 'Folgt ✓' : 'Folgen';
          btn.title = isFollowing
            ? 'Ansicht wieder selbst steuern'
            : `Ansicht von „${p.name}“ spiegeln`;
          btn.onclick = () => this.actions.onFollowToggle(p.clientId);
          chip.appendChild(btn);

          const inviteBtn = document.createElement('button');
          inviteBtn.type = 'button';
          inviteBtn.className = 'top-btn invite-btn';
          inviteBtn.textContent = 'Einladen';
          inviteBtn.title = `„${p.name}“ bitten, deiner Ansicht zu folgen`;
          inviteBtn.onclick = () => this.actions.onFollowInvite(p.clientId);
          chip.appendChild(inviteBtn);
        }
        return chip;
      })
    );
  }

  setStatus(status: CollabStatus, message?: string): void {
    const label =
      status === 'online' ? 'Verbunden — Änderungen werden live geteilt'
      : status === 'connecting' ? 'Verbinde …'
      : status === 'error' ? (message ?? 'Verbindung fehlgeschlagen')
      : (message ?? 'Nicht verbunden');
    this.statusText.textContent = label;
    this.statusText.dataset.status = status;
  }

  setError(message: string | null): void {
    this.errorText.textContent = message ?? '';
    this.errorText.hidden = !message;
  }
}
